import HexCoords from '../grid/HexCoords';
import HexPosition from '../components/HexPosition';
import type { Bearing } from '../grid/HexBearing';
import { ProposalType } from './Proposal';
import type { AnyEffect, Proposal } from './Proposal';
import type { Entity } from './entityManager';

export type ActionStatus = 'PENDING' | 'RESOLVED';

export default class Action {
  id: number;
  entity: Entity;
  type: ActionType;
  details: ActionDetails;
  sourceComponentName?: string;
  startPos: HexCoords | null;
  status: ActionStatus;
  outcomes: any[]; // Using 'any' as the structure is complex and not part of the request
  animationPlan: AnimationStep[][];
  minimumAnimationStage: number;
  isFallback: boolean;

  constructor(entity: Entity, type: ActionType, details: ActionDetails = {}, sourceComponentName?: string) {
    this.id = entity.id;
    this.entity = entity;
    this.type = type;
    this.details = details;
    this.sourceComponentName = sourceComponentName;
    this.startPos = entity.hasComponent(HexPosition) ? entity.getComponent(HexPosition)!.coords : null;
    this.status = 'PENDING';
    this.outcomes = [];
    this.animationPlan = [];
    this.minimumAnimationStage = 0;
    this.isFallback = false;
  }

  /**
   * Generates the default proposal for this action if no other proposals are provided.
   * This is used for basic actions like MOVE and TURN.
   */
  getDefaultProposal(): { priority: number, effects: AnyEffect[], baseProposalType: ProposalType } | null {
    if (this.type === ActionType.MOVE && this.details.coords) {
      return {
        priority: 1, // Default move priority
        effects: [{
          type: 'MOVE',
          coords: this.details.coords,
          animationPlan: [[{ type: 'MOVE', entityId: this.entity.id, targetCoords: this.details.coords }]],
        }],
        baseProposalType: ProposalType.REPLACEMENT,
      }
    } else if (this.type === ActionType.TURN && this.details.bearing !== undefined) {
      return {
        priority: 0, // Default turn priority
        effects: [{
          type: 'TURN',
          bearing: this.details.bearing,
          animationPlan: [[{ type: 'TURN', entityId: this.entity.id, bearing: this.details.bearing }]],
        }],
        baseProposalType: ProposalType.REPLACEMENT,
      }
    } else if (this.type === ActionType.IDLE) {
      return {
        priority: 0, // Default idle priority
        effects: [{ type: 'IDLE' }],
        baseProposalType: ProposalType.REPLACEMENT,
      }
    }
    return null;
  }

  resolve(): asserts this is ResolvedAction {
    this.status = 'RESOLVED';
  }

  /**
   * Forces the action to an IDLE state, clearing any existing outcomes and animations.
   */
  forceIdle(): void {
    if (this.status === 'RESOLVED') throw new Error('Cannot modify a resolved action.');
    this.outcomes = [{ type: ActionType.IDLE }];
    this.animationPlan = [];
  }

  addIdleOutcome(): void {
    if (this.status === 'RESOLVED') throw new Error('Cannot modify a resolved action.');
    this.outcomes.push({ type: ActionType.IDLE });
  }

  addMoveOutcome(coords: HexCoords): void {
    if (this.status === 'RESOLVED') throw new Error('Cannot modify a resolved action.');
    this.outcomes.push({ type: ActionType.MOVE, coords });
  }

  addTurnOutcome(bearing: Bearing): void {
    if (this.status === 'RESOLVED') throw new Error('Cannot modify a resolved action.');
    this.outcomes.push({ type: ActionType.TURN, bearing });
  }

  addAttackOutcome(target: any, damage: number): void {
    // attack outcomes can be added at the end of the tick after resolution, e.g. for attackTiming: 'END' attacks
    //if (this.status === 'RESOLVED') throw new Error('Cannot modify a resolved action.');
    this.outcomes.push({ type: ActionType.ATTACK, target, damage });
  }

  getMergedDefinition(allProposals: { action: Action; proposal: Proposal }[]): { priority: number; effects: AnyEffect[]; augmentProposals: Proposal[] } | null {
    const actionProposals = allProposals
      .filter(p => p.action.entity.id === this.entity.id)
      .map(p => p.proposal)
      .sort((a, b) => b.priority - a.priority);

    let baseProposal;
    if (this.isFallback) {
      // In fallback mode, we ignore any REPLACEMENT proposals and use the default.
      baseProposal = this.getDefaultProposal();
    } else {
      const replacementProposals = actionProposals.filter(p => p.type === ProposalType.REPLACEMENT);
      baseProposal = replacementProposals.length > 0 ? replacementProposals[0] : this.getDefaultProposal();
    }

    if (!baseProposal) return null;

    const baseEffects = baseProposal.effects;
    let highestPriority = baseProposal.priority;

    const augmentProposals = actionProposals.filter(p => p.type === ProposalType.AUGMENT);
    const deferredAugments: Proposal[] = [];
    const defensiveAugmentEffects: AnyEffect[] = [];

    for (const augment of augmentProposals) {
      const isPurelyDefensive = augment.effects.length > 0 && augment.effects.every(e => e.type === 'BLOCK');

      if (isPurelyDefensive) {
        defensiveAugmentEffects.push(...augment.effects);
      } else {
        deferredAugments.push(augment);
        highestPriority = Math.max(highestPriority, augment.priority);
      }
    }

    return {
      priority: highestPriority,
      effects: [...baseEffects, ...defensiveAugmentEffects],
      augmentProposals: deferredAugments
    };
  }

  addMissOutcome(target: any): void {
    // miss outcomes can be added at the end of the tick after resolution, e.g. for attackTiming: 'END' attacks
    //if (this.status === 'RESOLVED') throw new Error('Cannot modify a resolved action.');
    this.outcomes.push({ type: ActionType.MISS, target });
  }

  /**
   * Removes any MOVE outcome from the action. If this leaves the action with no
   * outcomes, it forces the action to IDLE. Critically, it also clears any
   * associated animation plan, preventing visual bugs.
   */
  removeMoveOutcome(): void {
    if (this.status === 'RESOLVED') throw new Error('Cannot modify a resolved action.');
    const moveOutcomeIndex = this.outcomes.findIndex(o => o.type === ActionType.MOVE);
    if (moveOutcomeIndex !== -1) {
      this.outcomes.splice(moveOutcomeIndex, 1);
      // A move was removed, so any associated animation is now invalid.
      this.animationPlan = [];
    }

    if (this.outcomes.length === 0) {
      this.forceIdle();
    }
  }

  toString(): string {
    let details = '';
    if (this.details.coords) {
      details = ` coords:${this.details.coords.toString()}`;
    } else if (this.details.bearing !== undefined) {
      details = ` bearing:${this.details.bearing}`;
    }
    const startPosStr = this.startPos ? ` from:${this.startPos.toString()}` : '';
    return `<Action (${this.entity.toString()}) type:${this.type}${details}${startPosStr}>`;
  }

  toJSON(): { type: ActionType, details: ActionDetailsJSON, sourceComponentName?: string } {
    const jsonDetails: ActionDetailsJSON = {};
    if (this.details.coords) {
      jsonDetails.coords = { q: this.details.coords.q, r: this.details.coords.r };
    }
    if (this.details.bearing !== undefined) {
      jsonDetails.bearing = this.details.bearing;
    }
    if (this.details.targetId !== undefined) {
      jsonDetails.targetId = this.details.targetId;
    }
    // Note: details.target is not serialized.

    return {
      type: this.type,
      details: jsonDetails,
      sourceComponentName: this.sourceComponentName,
    };
  }

  static fromJSON(data: { type: ActionType, details: ActionDetailsJSON, sourceComponentName?: string }, entity: any): Action {
    const details: ActionDetails = {};
    if (data.details.coords) {
      details.coords = HexCoords.get(data.details.coords);
    }
    if (data.details.bearing !== undefined) {
      details.bearing = data.details.bearing;
    }
    if (data.details.targetId !== undefined) {
      details.targetId = data.details.targetId;
    }
    return new Action(entity, data.type, details, data.sourceComponentName);
  }

  /**
   * Serializes the action with coordinates relative to the given player position.
   * This is used for solutions to remain valid after level recentering.
   */
  toRelativeJSON(playerPos: HexCoords): { type: ActionType, details: ActionDetailsJSON, sourceComponentName?: string } {
    const jsonDetails: ActionDetailsJSON = {};
    if (this.details.coords) {
      // Store as offset from player position
      jsonDetails.coords = {
        q: this.details.coords.q - playerPos.q,
        r: this.details.coords.r - playerPos.r
      };
    }
    if (this.details.bearing !== undefined) {
      jsonDetails.bearing = this.details.bearing;
    }
    if (this.details.targetId !== undefined) {
      jsonDetails.targetId = this.details.targetId;
    }
    // Note: details.target is not serialized.

    return {
      type: this.type,
      details: jsonDetails,
      sourceComponentName: this.sourceComponentName,
    };
  }

  /**
   * Deserializes an action from relative coordinates format.
   * Converts relative offsets back to absolute coordinates using the given player position.
   */
  static fromRelativeJSON(data: { type: ActionType, details: ActionDetailsJSON, sourceComponentName?: string }, entity: any, playerPos: HexCoords): Action {
    const details: ActionDetails = {};
    if (data.details.coords) {
      // Convert relative offset to absolute coords
      details.coords = HexCoords.get({
        q: data.details.coords.q + playerPos.q,
        r: data.details.coords.r + playerPos.r
      });
    }
    if (data.details.bearing !== undefined) {
      details.bearing = data.details.bearing;
    }
    if (data.details.targetId !== undefined) {
      details.targetId = data.details.targetId;
    }
    return new Action(entity, data.type, details, data.sourceComponentName);
  }

  static isEquivalent(a: Action | null, b: Action | null): boolean {
    if (!a || !b || a.type !== b.type) return false;

    switch (a.type) {
      case ActionType.MOVE:
      case ActionType.ATTACK:
      case ActionType.RANGED_ATTACK:
        return !!(a.details.coords && b.details.coords && a.details.coords.is(b.details.coords));
      case ActionType.TURN:
        return a.details.bearing === b.details.bearing;
      default:
        // For actions like IDLE that don't have details to compare
        return true;
    }
  }
}


export enum ActionMode {
  STANDARD = 'STANDARD',
  RANGED_ATTACK = 'RANGED_ATTACK',
  VISUALIZE = 'VISUALIZE',
}

export enum ActionType {
  MOVE = 'MOVE',
  ATTACK = 'ATTACK',
  IDLE = 'IDLE',
  TURN = 'TURN',
  RANGED_ATTACK = 'RANGED_ATTACK',
  MISS = 'MISS',
}

export type ActionDetails = {
  coords?: HexCoords;
  bearing?: Bearing;
  target?: any; // The intended entity target of an action.
  targetId?: number;
};

// This type is used for deserializing from plain JSON objects.
export type ActionDetailsJSON = {
  coords?: { q: number, r: number };
  bearing?: Bearing;
  targetId?: number;
  // `target` is not serialized as it's a complex object.
  // It is expected to be re-calculated by systems like AI if needed.
};

/**
 * A single, concrete result of a resolved action, e.g., an entity taking damage.
 * The `outcomes` list in an `OutcomeProposal` is an array of these.
 */
export type Outcome = {
  /** The type of outcome, e.g., 'ATTACK', 'MOVE', 'IDLE'. */
  type: ActionType;
  /** The entity that is the target of this outcome (e.g., for an attack). */
  target?: any; // Using 'any' for Entity to avoid import issues.
  /** The amount of damage to be dealt. */
  damage?: number;
  /** The destination coordinates for a 'MOVE' outcome. */
  coords?: HexCoords;
  /** For an `ATTACK` outcome, the hex where the attack lands. */
  targetCoords?: HexCoords;
};

/**
 * An object describing a single step in an animation sequence.
 */
export type AnimationStep = {
  type: string;
  entityId: number;
  targetCoords?: HexCoords;
  sourceCoords?: HexCoords;
  bearing?: Bearing;
  // TODO: Add other animation-related properties as needed.
};


/**
 * A type representing an Action that has been fully resolved
 * and is ready for its outcomes to be executed.
 */
export type ResolvedAction = Action & { status: 'RESOLVED' };
